import { useDispatch, useSelector } from "react-redux";
import React, { MouseEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HeaderComponent } from "../../../../../../components/header/header.component";
import { useQuery } from "../../../../../../utils/hooks/use-query";
import { useCanGoBack } from "../../../../../../utils/hooks/use-can-go-back";
import { OnboardingExamRegistryStatus, Timeslot } from "../../../../types";
import { dropdownStyles } from "../../../../components/surveyjs/dropdown/dropdown.style";
import * as commonStyle from "../../../../components/surveyjs/survey/common.style";
import { FormControl, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import moduleStyle from "./style.module.css";
import { ConfirmButton } from "../../../../../../components/buttons";
import { confirm } from "../../../../../../components/modal/comfirm";
import Icon from "../../../../../../components/icon/icon";
import { SvgAssets } from "../../../../../../components/icon/assets";
import {
  fetchTrainingExamByIdAsync,
  fetchTrainingExamCentresAsync,
  fetchTrainingExamTimeslotsAsync,
  postCandidateExamRegistryAsync,
} from "../../../../redux/onboarding-slice";
import {
  selectOnboardingCurrentExam,
  selectOnboardingExamCentres,
  selectOnboardingExamTimeslots,
} from "../../../../redux/selector";
import { PruToast } from "../../../../../../components/pru-toast";
import useThrottledTouchEnd from "../../../../hooks/use-throtted-touch-end";

type ExamRegistryScreenProps = {};
export const ExamRegistryScreen = (props: ExamRegistryScreenProps) => {
  useThrottledTouchEnd();
  const { t } = useTranslation();
  const translate = useMemo(
    () => ({
      application: t("Recruitment.onboarding_application"),
    }),
    [t],
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const { examId } = useParams();
  const candidateId = query.get("candidateId")!;
  const currentExam = useSelector(selectOnboardingCurrentExam);
  const fetchData = useCallback(async () => {
    if (!examId || !candidateId) return;
    await dispatch<any>(fetchTrainingExamByIdAsync({ candidateId, examId: examId! }));
  }, [candidateId, dispatch, examId]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const [examCentre, setExamCentre] = useState(currentExam?.registeredInfo?.examCentre ?? "");
  const [timeslot, setTimeslot] = useState(currentExam?.registeredInfo?.examScheduleId ?? "");
  const examCentrePools = useSelector(selectOnboardingExamCentres);
  const examTimeslotPools = useSelector(selectOnboardingExamTimeslots);
  useEffect(() => {
    if (!examCentrePools?.length || !currentExam?.registeredInfo?.examCentre) return;
    const selectedExamCentre = examCentrePools.find(
      (item) => item.examCentre === currentExam?.registeredInfo?.examCentre,
    )?.examCentre;
    setExamCentre(selectedExamCentre as string);
  }, [currentExam?.registeredInfo?.examCentre, examCentrePools]);
  useEffect(() => {
    if (!examTimeslotPools?.length || !currentExam?.registeredInfo?.examScheduleId) return;
    const selectedExamTimeslot = examTimeslotPools.find(
      (item) => item.value === currentExam?.registeredInfo?.examScheduleId,
    )?.value;
    setTimeslot(selectedExamTimeslot as string);
  }, [currentExam?.registeredInfo?.examScheduleId, examTimeslotPools]);
  const fetchExamCentres = useCallback(async () => {
    if (!currentExam) return;
    await dispatch<any>(fetchTrainingExamCentresAsync(currentExam));
  }, [currentExam, dispatch]);
  useEffect(() => {
    fetchExamCentres();
  }, [fetchExamCentres]);
  const fetchExamTimeslots = useCallback(async () => {
    if (!examCentre || !currentExam) return;
    await dispatch<any>(fetchTrainingExamTimeslotsAsync({ ...currentExam, examCentre } as any));
  }, [currentExam, examCentre, dispatch]);
  useEffect(() => {
    fetchExamTimeslots();
  }, [fetchExamTimeslots]);
  const examCentreChangeHandler = useCallback((event: SelectChangeEvent) => {
    setExamCentre(event.target.value);
  }, []);
  const timeslotChangeHandler = useCallback((event: SelectChangeEvent) => {
    setTimeslot(event.target.value);
  }, []);

  const selectedTimeslot: Timeslot | undefined = useMemo(() => {
    const timeslotItem: Timeslot | undefined = examTimeslotPools.find(
      (item) => item.value === timeslot,
    );
    return timeslotItem;
  }, [examTimeslotPools, timeslot]);
  const messageComp = useMemo(() => {
    const timeslotText = selectedTimeslot?.name ?? "";
    const remainingQuota = selectedTimeslot?.remainingQuota ?? 0;
    return (
      <div className="items-center">
        <div className="text-center pb-6">{t("registry_selection_informed")}</div>
        <div className="text-center pb-6">
          {t("timeslot_exam_center_selected", {
            timeslot: timeslotText,
            examCentre,
          })}
        </div>
        <div className="text-center">
          {t("timeslot_session_remaining_quota", { quota: remainingQuota })}
        </div>
      </div>
    );
  }, [selectedTimeslot, examCentre]);
  const formDisabled = useMemo(() => {
    return (
      [OnboardingExamRegistryStatus.REGISTERED].includes(currentExam?.status as any) ||
      currentExam?.readonly
    );
  }, [currentExam?.status]);
  const buttonIsDisabled = useMemo(() => {
    return !examCentre || !timeslot || formDisabled;
  }, [examCentre, timeslot, formDisabled]);
  const onSubmitHandler: MouseEventHandler = useCallback(
    (event) => {
      confirm({
        message: messageComp,
        title: t("reminder_title"),
        confirmButtonText: t("global.text.confirm"),
        icon: <Icon svg={SvgAssets.notice} width={80} height={80} />,
        confirmationCheckboxLabel: t("timeslot_unchanged_after_register"),
      }).then(async (result) => {
        if (result.ok) {
          const actionResponse = await dispatch<any>(
            postCandidateExamRegistryAsync({
              candidateId,
              examScheduleId: timeslot,
            }),
          );
          const errors = actionResponse?.error ?? actionResponse?.payload?.errors;
          if (!errors) {
            PruToast({
              status: "success",
              message: t("common.success"),
            });
            if (canGoBack()) navigate(-1);
          } else {
            PruToast({
              message: t(errors?.[0]?.message),
            });
          }
        }
      });
    },
    [timeslot, examCentre, messageComp],
  );
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  return (
    <div className="h-screen flex flex-col">
      <HeaderComponent
        title={t("registration")}
        onBack={() => {
          if (canGoBack()) {
            navigate(-1);
          }
        }}
      />
      {currentExam && (
        <>
          <div className="flex-1 flex flex-col w-screen bg-gray-100">
            <div className="text-grey33 text-xl font-semibold p-6">{currentExam?.name}</div>
            <div className="flex flex-col justify-center items-start py-6 px-4 mx-4 gap-4 rounded-xl bg-white">
              <div className="flex w-full flex-1 flex-col">
                <div className="pb-2">
                  {t("please_choose_exam_centre")}
                  <span className="text-red-500">*</span>
                </div>

                <FormControl fullWidth sx={commonStyle.formControl} variant="standard">
                  {formDisabled ? (
                    <div className="text-gray-500 text-base font-medium py-2 border-b border-gray-200">
                      {currentExam?.registeredInfo?.examCentre ?? ""}
                    </div>
                  ) : (
                    <Select
                      style={dropdownStyles.root}
                      value={examCentre}
                      disabled={formDisabled}
                      key={"examCentre"}
                      onChange={examCentreChangeHandler}
                      placeholder={"this.placeholder"}
                      variant="outlined"
                      // onFocus={this.question.onFocus}
                      id={"examCentre"}
                      MenuProps={{
                        style: dropdownStyles.menuMaxHeight,
                      }}
                      size="small"
                    >
                      {examCentrePools.map((item: any, index: number) => {
                        return (
                          <MenuItem
                            classes={{
                              selected: moduleStyle.dropdownSelected,
                            }}
                            key={item.examCentre}
                            value={item.examCentre}
                            sx={dropdownStyles.menuItem}
                          >
                            {item.examCentre}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                </FormControl>
              </div>
              <div className="flex w-full flex-1 flex-col">
                <div className="pb-2">
                  {t("please_select_available_timeslot")}
                  <span className="text-red-500">*</span>
                </div>
                <FormControl fullWidth sx={commonStyle.formControl} variant="standard">
                  {formDisabled ? (
                    <div className="text-gray-500 text-base font-medium py-2 border-b border-gray-200">
                      {currentExam?.registeredInfo?.examDate
                        ? `${currentExam?.registeredInfo?.examDate},${currentExam?.registeredInfo?.startTime}-${currentExam?.registeredInfo?.endTime}`
                        : ""}
                    </div>
                  ) : (
                    <Select
                      style={dropdownStyles.root}
                      value={timeslot}
                      disabled={formDisabled}
                      key={"timeslot"}
                      onChange={timeslotChangeHandler}
                      placeholder={"this.placeholder"}
                      variant="outlined"
                      // onFocus={this.question.onFocus}
                      id={"timeslot"}
                      MenuProps={{
                        style: dropdownStyles.menuMaxHeight,
                      }}
                      size="small"
                    >
                      {examTimeslotPools.map((item: Timeslot, index: number) => {
                        return (
                          <MenuItem
                            classes={{
                              selected: moduleStyle.dropdownSelected,
                            }}
                            key={index}
                            value={item.value}
                            sx={dropdownStyles.menuItem}
                          >
                            {item.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                </FormControl>
              </div>
            </div>
          </div>
          <div className="flex w-full px-4 p-6 bg-gray-100">
            <ConfirmButton disabled={buttonIsDisabled} onClick={onSubmitHandler}>
              {t("proceed")}
            </ConfirmButton>
          </div>
        </>
      )}
    </div>
  );
};
